import React, { useState } from 'react';
import KeyPointMiniMap from './KeyPointMiniMap';
import { 
  Brain, Clock, AlertTriangle, Package, Droplet, 
  Mountain, Cloud, TrendingUp, ChevronDown, 
  ChevronUp, Download, Zap, ShieldCheck, Thermometer, Settings
} from 'lucide-react';
import '../styles/AIAnalysis.css';

const AIAnalysis = ({ analysis, onDownloadGPX }) => {
  const [expandedSections, setExpandedSections] = useState({
    pacing: true,
    keyPoints: true,
    nutrition: true,
    safety: true,
    equipment: true,
    weather: true,
    strava: false,
	coordinates: true
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // FUNCIÓ DE SEGURETAT: Evita l'error .map() si la dada no és un Array
  const ensureArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') return [data];
    return [];
  };

  if (!analysis) {
    return (
      <div className="ai-analysis-container">
        <div className="no-analysis">
          <Brain size={48} />
          <p>Esperant anàlisi detallada de la ruta...</p>
        </div>
      </div>
    );
  }

  const renderTaggedDescription = (description, tag) => {
    const getTagStyle = (tagStr) => {
      const s = tagStr?.toUpperCase() || '';
      if (s.includes('ATTACK')) return { color: '#e11d48', bg: '#fff1f2', label: '🔥 ATTACK' };
      if (s.includes('FLOW')) return { color: '#059669', bg: '#f0fdf4', label: '🔋 FLOW' };
      if (s.includes('CRUISE')) return { color: '#2563eb', bg: '#eff6ff', label: '⚙️ CRUISE' };
      return { color: '#64748b', bg: '#f1f5f9', label: tagStr || 'INFO' };
    };

    const style = getTagStyle(tag);

    return (
      <div className="description-container">
        <span className="effort-badge" style={{ color: style.color, backgroundColor: style.bg, padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-block', marginBottom: '8px' }}>
          {style.label}
        </span>
        <p className="description-text" style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5' }}>{description}</p>
      </div>
    );
  };

  const getDifficultyColor = (difficulty) => {
    const d = difficulty?.toLowerCase() || '';
    if (d.includes('difícil') || d.includes('expert') || d.includes('alta')) return '#ef4444';
    if (d.includes('mitjà') || d.includes('moderada')) return '#f59e0b';
    return '#22c55e';
  };

  const getTempColor = (temp) => {
    if (temp < 8) return '#3b82f6';
    if (temp < 12) return '#06b6d4';
    if (temp < 18) return '#10b981';
    if (temp < 25) return '#f59e0b';
    return '#ef4444';
  };

  const getConditionIcon = (conditions) => {
    const lower = conditions.toLowerCase();
    if (lower.includes('pluja') || lower.includes('xàfec')) return '🌧️';
    if (lower.includes('ennuvolat')) return '☁️';
    if (lower.includes('clar')) return '☀️';
    return '🌤️';
  };

  return (
    <div className="ai-analysis-container fade-in">
      {/* Header */}
      <div className="analysis-header">
        <div className="header-icon"><Brain size={32} /></div>
        <div className="header-content">
          <h2>Roadbook Estratègic</h2>
          <p>Anàlisi avançada Gemini 2.0 • Nutrició • Pacing • Seguretat</p>
        </div>
      </div>

      {/* Highlights */}
      <div className="highlights-grid">
        <div className="highlight-card" style={{ borderLeft: `4px solid ${getDifficultyColor(analysis.difficulty_rating)}` }}>
          <Mountain size={24} />
          <div className="highlight-content">
            <span className="highlight-label">Anàlisi d'Esforç</span>
            <span className="highlight-value">{analysis.difficulty_rating}</span>
          </div>
        </div>
        <div className="highlight-card">
          <Clock size={24} />
          <div className="highlight-content">
            <span className="highlight-label">Temps Objectiu</span>
            <span className="highlight-value">{analysis.estimated_time}</span>
          </div>
        </div>
      </div>

      {/* 1. Estratègia de Ritme */}
      {analysis.pacing_strategy && (
        <Section title="Estratègia de Ritme" icon={<Zap size={20} />} isExpanded={expandedSections.pacing} onToggle={() => toggleSection('pacing')}>
          <div className="pacing-box" style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', borderLeft: '4px solid #6366f1' }}>
            <p style={{ margin: 0 }}>{analysis.pacing_strategy}</p>
          </div>
        </Section>
      )}

      {/* 2. Punts Clau - Roadbook */}
      <Section title="Roadbook per Sectors" icon={<TrendingUp size={20} />} isExpanded={expandedSections.keyPoints} onToggle={() => toggleSection('keyPoints')}>
        <div className="key-points-list">
          {ensureArray(analysis.key_points).map((point, index) => (
            <div key={index} className="key-point-item-advanced" style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', gap: '15px' }}>
                <span style={{ background: '#1e293b', color: 'white', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', height: 'fit-content' }}>
                  KM {point.km}
                </span>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1.05rem' }}>{point.title || 'Punt de Ruta'}</h4>
                  {renderTaggedDescription(point.description, point.tag)}
					
					{/* MINIMAPA */}
					{point.lat && point.lon && (
					 <KeyPointMiniMap 
					 lat={point.lat} 
					 lon={point.lon} 
					 km={point.km}
					 routeCoordinates={analysis.coordinates} // Passar des de props
					 pointRadius={2}
					 />
					)}
					
					
                  {point.gear_advice && (
                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>
                      <Settings size={14} /> <span>{point.gear_advice}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 3. Pla de Nutrició */}
      {analysis.nutrition_plan && (
        <Section title="Estratègia d'Aigua i Energia" icon={<Droplet size={20} />} isExpanded={expandedSections.nutrition} onToggle={() => toggleSection('nutrition')}>
          <div className="nutrition-list">
            {ensureArray(analysis.nutrition_plan).map((item, index) => (
              <div key={index} className="nutrition-card-item" style={{ background: '#f0f9ff', padding: '10px', borderRadius: '8px', marginBottom: '8px', borderLeft: '4px solid #0ea5e9' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#0369a1' }}>{item}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* 4. Termodinàmica / Meteo - VERSIÓ AMPLIADA */}
      {(analysis.weather_recommendations || analysis.weather_strategy || analysis.gear_by_zone) && (
        <Section 
          title="Gestió Tèrmica i Equipament" 
          icon={<Thermometer size={20} />} 
          isExpanded={expandedSections.weather} 
          onToggle={() => toggleSection('weather')}
        >
          {/* Recomanacions generals */}
          {analysis.weather_recommendations && (
            <div className="weather-general" style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '0.9rem', color: '#475569', margin: '0 0 10px 0', fontWeight: 700 }}>📋 Recomanacions Generals</h4>
              <ul className="weather-list" style={{ margin: 0, paddingLeft: '20px' }}>
                {ensureArray(analysis.weather_recommendations).map((rec, index) => (
                  <li key={index} style={{ marginBottom: '6px', color: '#64748b' }}>{rec}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Estratègia per zones (weather_strategy) */}
          {analysis.weather_strategy && (
            <div className="weather-zones" style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '0.9rem', color: '#475569', margin: '0 0 10px 0', fontWeight: 700 }}>📍 Condicions per Zona</h4>
              {ensureArray(analysis.weather_strategy).map((alert, index) => {
                const tempMatch = alert.match(/(\d+\.?\d*)°C/);
                const temp = tempMatch ? parseFloat(tempMatch[1]) : null;
                
                return (
                  <div 
                    key={index} 
                    className="zone-alert" 
                    style={{ 
                      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
                      padding: '10px 12px', 
                      borderRadius: '8px', 
                      marginBottom: '8px',
                      borderLeft: `3px solid ${temp ? getTempColor(temp) : '#64748b'}`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.85rem' }}>
                        {alert.split(':')[0]}
                      </span>
                      {temp && (
                        <span 
                          style={{ 
                            backgroundColor: getTempColor(temp), 
                            color: 'white', 
                            padding: '2px 8px', 
                            borderRadius: '12px', 
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Thermometer size={12} />
                          {temp}°C
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, color: '#475569', fontSize: '0.85rem' }}>
                      {alert.split(': ').slice(1).join(': ')}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Equipament per tram (gear_by_zone) */}
          {analysis.gear_by_zone && (
            <div className="gear-zones">
              <h4 style={{ fontSize: '0.9rem', color: '#475569', margin: '0 0 10px 0', fontWeight: 700 }}>👕 Equipament Recomanat per Tram</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                {ensureArray(analysis.gear_by_zone).map((zone, index) => (
                  <div 
                    key={index} 
                    className="gear-card" 
                    style={{ 
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                      padding: '12px', 
                      borderRadius: '8px',
                      border: '1px solid #fbbf24'
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <span 
                        style={{ 
                          background: '#f59e0b', 
                          color: 'white', 
                          padding: '3px 8px', 
                          borderRadius: '6px', 
                          fontSize: '0.75rem', 
                          fontWeight: 700,
                          display: 'inline-block'
                        }}
                      >
                        Km {zone.km_range}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#92400e', marginBottom: '8px', fontWeight: 600 }}>
                      {getConditionIcon(zone.conditions)} {zone.conditions}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#78350f', lineHeight: '1.4' }}>
                      {zone.recommended_gear.split(',').map((item, i) => (
                        <div key={i} style={{ marginBottom: '3px' }}>• {item.trim()}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {/* 5. Seguretat */}
      {analysis.safety_warnings && (
        <Section title="Seguretat i Alertes" icon={<AlertTriangle size={20} />} isExpanded={expandedSections.safety} onToggle={() => toggleSection('safety')} variant="warning">
          <ul className="warning-list" style={{ color: '#991b1b' }}>
            {ensureArray(analysis.safety_warnings).map((warning, index) => (
              <li key={index} style={{ marginBottom: '6px' }}>{warning}</li>
            ))}
          </ul>
        </Section>
      )}

      {/* 6. Strava */}
      {analysis.strava_comparison && (
        <Section title="Anàlisi de Segments Strava" icon={<TrendingUp size={20} />} isExpanded={expandedSections.strava} onToggle={() => toggleSection('strava')}>
          <p className="strava-text" style={{ fontStyle: 'italic', color: '#475569' }}>{analysis.strava_comparison}</p>
        </Section>
      )}

      {onDownloadGPX && (
        <button className="download-gpx-button-pro" onClick={onDownloadGPX} style={{ width: '100%', marginTop: '20px', padding: '12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', fontWeight: 'bold', cursor: 'pointer' }}>
          <Download size={20} />
          Exportar Roadbook a GPX (Garmin/Wahoo)
        </button>
      )}
    </div>
  );
};

// Component Auxiliar per a les Seccions
const Section = ({ title, icon, children, isExpanded, onToggle, variant }) => {
  const isWarning = variant === 'warning';
  return (
    <div className={`analysis-section ${isWarning ? 'warning' : ''}`} style={{ marginBottom: '15px', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', background: isWarning ? '#fff7ed' : 'white' }}>
      <div className="section-header" onClick={onToggle} style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: isWarning ? '#ffedd5' : '#f8fafc' }}>
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {icon}
          <h3 style={{ margin: 0, fontSize: '1rem', color: isWarning ? '#9a3412' : '#1e293b' }}>{title}</h3>
        </div>
        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>
      {isExpanded && <div className="section-content" style={{ padding: '16px' }}>{children}</div>}
    </div>
  );
};

export default AIAnalysis;